import { defineStore } from "pinia";
import { ref, computed } from "vue";

interface LoadingTask {
  id: string;
  startTime: number;
}

export const useLoadingStore = defineStore("loading", () => {
  // State
  const activeTasks = ref<LoadingTask[]>([]);
  const isPageLoading = ref(false);
  const minimumDisplayDuration = 700;

  // Computed
  const isLoading = computed(() => {
    return activeTasks.value.length > 0 || isPageLoading.value;
  });

  const hasActiveTasks = computed(() => activeTasks.value.length > 0);

  // Actions
  function startTask(taskId: string) {
    const existingTask = activeTasks.value.find((t) => t.id === taskId);
    if (!existingTask) {
      activeTasks.value.push({
        id: taskId,
        startTime: Date.now(),
      });
    }
  }

  function endTask(taskId: string) {
    const taskIndex = activeTasks.value.findIndex((t) => t.id === taskId);
    if (taskIndex === -1) return;

    const task = activeTasks.value[taskIndex];
    const elapsedTime = Date.now() - task.startTime;

    // Enforce minimum display duration
    if (elapsedTime < minimumDisplayDuration) {
      setTimeout(() => {
        const index = activeTasks.value.findIndex((t) => t.id === taskId);
        if (index !== -1) {
          activeTasks.value.splice(index, 1);
        }
      }, minimumDisplayDuration - elapsedTime);
    } else {
      activeTasks.value.splice(taskIndex, 1);
    }
  }

  function clearAllTasks() {
    activeTasks.value = [];
  }

  function setPageLoading(loading: boolean) {
    isPageLoading.value = loading;
  }

  return {
    // State
    activeTasks,
    isPageLoading,

    // Computed
    isLoading,
    hasActiveTasks,

    // Actions
    startTask,
    endTask,
    clearAllTasks,
    setPageLoading,
  };
});
